'use client'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import Image from 'next/image'
import { type ReactNode } from 'react'

const QUALITY_ROWS = [
  {
    title: 'Fabric',
    description:
      'Each fabric is benchmarked against the global standard for its category — moisture management, abrasion resistance, colour fastness, dimensional stability.',
    image: '/image/2026/06/18/frame_87867.jpg',
    imageLeft: true,
  },
  {
    title: 'Wear test',
    description:
      'Every new product is worn-tested by our internal team, the Coolmate Running Club, and pickleball partners before we open the SKU to wider release.',
    image: '/image/2026/06/18/frame_87867_1.jpg',
    imageLeft: false,
  },
  {
    title: 'Construction',
    description:
      'Every batch is sampled for stitch strength, seam alignment, and finishing detail at the factory and again on receipt.',
    image: '/image/2026/06/18/frame_87867_2.jpg',
    imageLeft: true,
  },
  {
    title: 'Feedback loop',
    description:
      'Every product page accepts reviews. We read them. We update the product when a real signal emerges from the noise.',
    image: '/image/2026/06/18/frame_87867_3.jpg',
    imageLeft: false,
  },
]

export const QualitySystemDialog = ({ trigger }: { trigger: ReactNode }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className='no-scrollbar bottom-0 top-auto flex max-h-[90vh] w-full max-w-5xl translate-y-0 flex-col overflow-hidden rounded-b-none rounded-t-2xl p-0 max-md:left-0 max-md:translate-x-0 md:bottom-auto md:top-1/2 md:translate-y-[-50%] md:rounded-2xl'
      >
        {/* Sticky header */}
        <div className='sticky top-0 z-10 flex items-center justify-between bg-white px-8 pb-4 pt-8'>
          <DialogTitle className='font-criteria text-[28px] font-semibold leading-tight tracking-[-0.02em] text-neutral-900 md:text-[44px] md:leading-[55px]'>
            Our quality system
          </DialogTitle>
          <DialogClose className='rounded-sm opacity-70 transition-opacity hover:opacity-100'>
            <X className='h-5 w-5' />
            <span className='sr-only'>Close</span>
          </DialogClose>
        </div>

        <div className='no-scrollbar min-h-0 flex-1 overflow-y-auto px-8 pb-8'>
          <div className='flex flex-col gap-10'>
          {QUALITY_ROWS.map((row) => (
            <div
              key={row.title}
              className={cn(
                'grid grid-cols-1 items-center gap-3 md:gap-11',
                row.imageLeft
                  ? 'md:grid-cols-[7fr_5fr]'
                  : 'md:grid-cols-[5fr_7fr] md:[&>*:first-child]:order-2 md:[&>*:last-child]:order-1'
              )}
            >
              <div className='relative aspect-[3/2] w-full overflow-hidden rounded-lg'>
                <Image
                  src={ImageHelper.getImageUrl(row.image)}
                  alt={row.title}
                  fill
                  className='object-cover'
                />
              </div>
              <div className='flex flex-col gap-3'>
                <h3 className='font-criteria text-2xl font-semibold tracking-[-0.02em] text-neutral-900 md:text-4xl'>
                  {row.title}
                </h3>
                <p className='text-base leading-normal text-neutral-600 md:text-lg'>
                  {row.description}
                </p>
              </div>
            </div>
          ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
